import { useFloating, useHover, useInteractions } from '@floating-ui/react';
import { animated, SpringConfig, to, useSpring, useSpringRef, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { NeueAddSectionButton } from '../add-section-button';
import { getActiveSectionId } from '../helpers/get-active-section-id';
import { getSuperRect } from '../helpers/get-super-rect';
import { Block, BlockRenderElement, Rect, Section } from '../types';
import { useUpdateEffect } from 'react-use';
import { usePreviousValue } from 'src/utils/react/previous-value.hook';
import { createPropsEqualsChecker } from '../utils/create-props-equals-checker';
import { usePlayerStore } from './player-store';
import { NeuePlayerSectionCard } from './neue-player-section-card';
import { useScrollToSectionById } from './scroll-to-section-by-id.hook';
import { ScrollDirection, useScrollDirection } from '../utils/scroll-direction.hook';

export const WIDTH_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

type SectionCardData = {
  section: Section;
  // title: string;
  userProvidedTitle: Nullable<string>;
  // placeholder: boolean;
  blockRenderElements: BlockRenderElement[];
};

type SectionCardTransitionItem = {
  sectionCardData: SectionCardData;
  rect: Rect;
};

const SECTION_BAR_LAYOUT = {
  cardWidth: 272 + 24,
  cardHeight: 42,
  cardGap: 4,
  barMaxHeight: 520,
  barPadding: 4,
};

export const NeuePlayerSectionBar = () => {
  const {
    layout: { sections: allSections, renderElements, sectionLayoutInfos, innerActualHeight },
    innerAreaHeight,
    playerContentElement,
    canvasScrollPosition,
    currentSectionId,
    setCurrentSectionId,
    scrollToSectionId,
    setScrollToSectionId,
  } = usePlayerStore();
  const [lastScrollDirection, setLastScrollDirection] = React.useState<ScrollDirection>('up');

  const scrollToSectionById = useScrollToSectionById(() => {
    setScrollToSectionId(null);
  });

  const sections = allSections.filter((section) => !section.hiddenInNavigation);

  useScrollDirection(
    playerContentElement,
    (direction) => {
      if (scrollToSectionId) return;
      setLastScrollDirection(direction);
    },
    2
  );

  const [hovering, setHovering] = useState(false);
  const { refs, context } = useFloating({
    open: hovering,
    onOpenChange: setHovering,
  });
  const hover = useHover(context);
  const { getReferenceProps } = useInteractions([hover]);

  const renderElementsMap = useMemo(() => {
    const map = new Map<Block['id'], BlockRenderElement>();
    for (const re of renderElements) {
      if (re.type === 'block') {
        map.set(re.block.id, re);
      }
    }
    return map;
  }, [renderElements]);

  const updateActiveSectionId = useRefCallback(() => {
    if (!innerAreaHeight) return null;
    if (!innerActualHeight) return null;
    if (sections.length === 0) return null;
    const scrollBasedActiveId = getActiveSectionId({
      sections,
      sectionLayoutInfos,
      innerAreaHeight,
      innerActualHeight,
      canvasScrollPosition,
    });
    if (!scrollBasedActiveId) return;
    if (scrollToSectionId) return;
    if (scrollBasedActiveId === currentSectionId) return;
    // console.log('SectionBar: updateActiveSectionId:', 'active section', scrollBasedActiveId);
    setCurrentSectionId(scrollBasedActiveId);
  }, [innerActualHeight, sections, sectionLayoutInfos, canvasScrollPosition, currentSectionId, scrollToSectionId]);

  useEffect(() => {
    updateActiveSectionId();
  }, [canvasScrollPosition, innerActualHeight, sections.length, updateActiveSectionId]);

  React.useEffect(() => {
    if (scrollToSectionId) {
      scrollToSectionById(scrollToSectionId);
    }
  }, [scrollToSectionId]);

  // useEffect(() => {
  //   if (!currentOrganization) return;
  //   if (!journey) return;
  //   if (!currentSectionId) return;
  //   if (initialSectionId) return;
  //   const sectionRelativePath = sections.find((section) => section.id === currentSectionId)?.friendlyPath || '';
  //   const sectionRelativeUrl = generateJourneyNeueEditUrl(currentOrganization, journey, sectionRelativePath, {
  //     relative: true,
  //   });
  //   console.log('SectionBar: update url', sectionRelativeUrl, initialSectionId);
  //   window.history.replaceState({}, '', sectionRelativeUrl);
  // }, [currentSectionId, initialSectionId, currentOrganization, journey]);

  const sectionCardData: SectionCardData[] = useMemo(
    () =>
      sections.map((section) => {
        let userProvidedTitle = section.name;
        const blockRenderElements = section.blocks
          .map((block) => renderElementsMap.get(block.id))
          .filter(Boolean) as BlockRenderElement[];

        return {
          section,
          userProvidedTitle,
          blockRenderElements,
        };
      }),
    [sections, renderElementsMap]
  );

  const onSectionCardClick = (id: Section['id']) => {
    setCurrentSectionId(id);
    setScrollToSectionId(id);
  };

  const api = useSpringRef();
  const [props] = useSpring(
    () => ({
      ref: api,
      from: { width: 64 },
      to: { width: 280 },
    }),
    []
  );

  useEffect(() => {
    if (hovering) {
      api.start({ width: 280, config: WIDTH_ANIMATION_CONFIG });
    } else {
      api.start({ width: 64, config: WIDTH_ANIMATION_CONFIG });
    }
  }, [hovering]);

  const itemsRef = useRef(new Map<Section['id'], HTMLDivElement>());

  let y = SECTION_BAR_LAYOUT.barPadding;
  const sectionCardRects: Rect[] = useMemo(() => {
    const rects: Rect[] = [];
    for (let i = 0; i < sections.length; i++) {
      const rect = {
        x: 0,
        y,
        width: SECTION_BAR_LAYOUT.cardWidth,
        height: SECTION_BAR_LAYOUT.cardHeight,
      };
      rects.push(rect);
      y += rect.height;
      if (i < sections.length - 1) {
        y += SECTION_BAR_LAYOUT.cardGap;
      }
    }
    return rects;
  }, [sections]);

  let { width: totalWidth, height: totalHeight } = getSuperRect(sectionCardRects);

  const transitionData = useMemo(
    () =>
      sectionCardData.map((sectionCardDataItem, i) => {
        return {
          sectionCardData: sectionCardDataItem,
          rect: sectionCardRects[i],
        };
      }),
    [sectionCardData, sectionCardRects]
  );

  const [transitions, transitionApi] = useTransition<SectionCardTransitionItem, { y: number }>(transitionData, () => ({
    config: { mass: 1, tension: 400, friction: 40 },
    key: (item: SectionCardTransitionItem) => item.sectionCardData.section.id,
    from: (item: SectionCardTransitionItem) => {
      // console.log('transition from', item);
      return {
        y: item.rect.y,
      };
    },
  }));

  useUpdateEffect(() => {
    transitionApi.start();
  }, [sections]);

  if (sections.length === 0) {
    return null;
  }

  return (
    <animated.div
      className={classNames(
        'pointer-events-auto flex flex-col justify-start transition text-neue-journey-fg rounded-lg relative',
        'select-auto'
      )}
      style={{
        width: to(props.width, (width) => `${width + 24}px`),
      }}
    >
      <animated.div
        className='absolute top-0 left-6 w-16 h-full rounded-lg transition bg-neue-journey-bg'
        style={{
          width: to(props.width, (width) => `${width}px`),
        }}
      />
      <div className={classNames('flex flex-col flex-1 rounded-lg overflow-hidden isolate')}>
        <div className='relative'>
          <div
            className='relative overflow-y-auto scrollbar-hidden'
            style={{
              width: `${totalWidth}px`,
              height: `${Math.min(totalHeight, SECTION_BAR_LAYOUT.barMaxHeight) + 2 * SECTION_BAR_LAYOUT.barPadding}px`,
            }}
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            <div
              className='relative'
              style={{
                width: `${totalWidth}px`,
                height: `${totalHeight + 2 * SECTION_BAR_LAYOUT.barPadding}px`,
              }}
            >
              {transitions((style, item, _, i) => {
                const {
                  sectionCardData: {
                    section: { id, name },
                    blockRenderElements,
                  },
                } = item;
                return (
                  <NeuePlayerSectionCard
                    key={id}
                    ref={(el) => {
                      if (el) {
                        itemsRef.current.set(id, el);
                      } else {
                        itemsRef.current.delete(id);
                      }
                    }}
                    style={style}
                    rect={item.rect}
                    id={id}
                    index={i}
                    showIndices={hovering}
                    title={name || 'Untitled'}
                    current={id === currentSectionId}
                    blockRenderElements={blockRenderElements}
                    onCardClick={() => onSectionCardClick(id)}
                  />
                );
              })}
            </div>
          </div>
          <div
            className='z-neue-section-bar-borders pointer-events-none absolute top-0 left-6 w-16 rounded-lg border-[4px] transition border-neue-journey-bg'
            style={{
              height: `${Math.min(totalHeight, SECTION_BAR_LAYOUT.barMaxHeight) + 2 * SECTION_BAR_LAYOUT.barPadding}px`,
            }}
          ></div>
          <div
            className='z-neue-section-bar-borders pointer-events-none absolute top-0 left-6 rounded-lg border-[4px] transition border-neue-journey-bg'
            style={{
              width: `${totalWidth}px`,
              height: `${Math.min(totalHeight, SECTION_BAR_LAYOUT.barMaxHeight) + 2 * SECTION_BAR_LAYOUT.barPadding}px`,
            }}
          ></div>
          <div
            className='z-neue-section-bar-borders pointer-events-none absolute top-0 left-0 w-6 rounded-lg border-[4px] transition-colors border-neue-canvas-bg'
            style={{
              height: `${Math.min(totalHeight, SECTION_BAR_LAYOUT.barMaxHeight) + 2 * SECTION_BAR_LAYOUT.barPadding}px`,
            }}
          ></div>
        </div>
      </div>
    </animated.div>
  );
};

export const MemoizedNeuePlayerSectionBar = React.memo(
  NeuePlayerSectionBar,
  createPropsEqualsChecker([], 'SectionBar')
);
